import { useEffect, useState } from 'react';
import { Wrench, Clock, CalendarClock, ArrowRight } from 'lucide-react';
import { CleviaLogo } from '@/components/CleviaLogo';
import { HeroBackdrop } from '@/components/landing/page-kit';
import type { MaintenanceMode } from '@/contexts/FeatureFlagContext';

interface MaintenancePageProps {
  maintenance: MaintenanceMode;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CountdownTimer({ endTime }: { endTime: string | null }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (!endTime) return;

    const tick = () => {
      const now = Date.now();
      const end = new Date(endTime).getTime();
      const diff = end - now;

      if (diff <= 0) {
        setTimeLeft('');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      const parts: string[] = [];
      if (days > 0) parts.push(`${days}j`);
      parts.push(`${hours.toString().padStart(2, '0')}h`);
      parts.push(`${minutes.toString().padStart(2, '0')}m`);
      parts.push(`${seconds.toString().padStart(2, '0')}s`);
      setTimeLeft(parts.join(' '));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  if (!endTime || !timeLeft) return null;

  return (
    <div className="mt-10 rounded-xl border border-border bg-card px-8 py-6">
      <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
        Retour estimé dans
      </p>
      <p className="mt-3 text-4xl font-semibold tabular-nums tracking-tight sm:text-5xl">
        {timeLeft}
      </p>
    </div>
  );
}

export default function MaintenancePage({ maintenance }: MaintenancePageProps) {
  const schedule = [
    maintenance.start_time && {
      icon: CalendarClock,
      label: 'Début',
      value: formatDateTime(maintenance.start_time),
    },
    maintenance.end_time && {
      icon: Clock,
      label: 'Fin estimée',
      value: formatDateTime(maintenance.end_time),
    },
  ].filter(Boolean) as { icon: typeof Clock; label: string; value: string }[];

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <HeroBackdrop />

      <div className="mx-auto max-w-2xl text-center">
        <CleviaLogo size="lg" className="justify-center" />

        <div className="mt-10 flex justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-card">
            <Wrench
              className="h-5 w-5 text-primary motion-safe:animate-[spin_8s_linear_infinite] dark:text-brand-400"
              aria-hidden="true"
            />
          </span>
        </div>

        <h1 className="mt-8 text-balance text-3xl font-semibold leading-[1.12] tracking-tight sm:text-4xl lg:text-[2.75rem]">
          Maintenance en cours
        </h1>

        <p className="mx-auto mt-5 max-w-lg text-pretty leading-relaxed text-muted-foreground">
          {maintenance.message ||
            'Nous effectuons des améliorations pour vous offrir une meilleure expérience. Merci de votre patience !'}
        </p>

        {schedule.length > 0 && (
          <div className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:flex-row">
            {schedule.map((item, i) => (
              <div
                key={item.label}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-5 py-4 text-left sm:min-w-[240px]"
              >
                <item.icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span>
                  <span className="block text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    {item.label}
                  </span>
                  <span className="mt-1 block text-sm">{item.value}</span>
                </span>
                {i === 0 && schedule.length > 1 && (
                  <ArrowRight
                    className="ml-auto hidden h-4 w-4 shrink-0 text-muted-foreground sm:block"
                    aria-hidden="true"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <CountdownTimer endTime={maintenance.end_time} />

        {/* Fil de progression indéterminé */}
        <div className="mx-auto mt-12 h-px max-w-xs overflow-hidden bg-border">
          <div
            className="h-full bg-primary"
            style={{ animation: 'maintenance-progress 2.5s ease-in-out infinite' }}
          />
        </div>

        <p className="mt-12 text-sm text-muted-foreground">
          clevia — Votre assistant financier intelligent
        </p>
      </div>

      <style>{`
        @keyframes maintenance-progress {
          0% { width: 0%; margin-left: 0; }
          50% { width: 100%; margin-left: 0; }
          100% { width: 0%; margin-left: 100%; }
        }
      `}</style>
    </div>
  );
}
